"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

interface GraphContextValue {
  open: boolean;
  /** True once the user has opened the overlay at least once — used to lazy-
   *  mount the GeoGebra iframe so unrelated pages don't pay the load cost. */
  everOpened: boolean;
  openGraph: () => void;
  closeGraph: () => void;
  toggleGraph: () => void;
}

const Ctx = createContext<GraphContextValue | null>(null);

export function GraphProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);

  const openGraph = useCallback(() => {
    setOpen(true);
    setEverOpened(true);
  }, []);
  const closeGraph = useCallback(() => setOpen(false), []);
  const toggleGraph = useCallback(() => {
    setOpen((o) => {
      if (!o) setEverOpened(true);
      return !o;
    });
  }, []);

  return (
    <Ctx.Provider
      value={{ open, everOpened, openGraph, closeGraph, toggleGraph }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useGraph(): GraphContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useGraph must be used inside GraphProvider");
  return v;
}
